
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'guide',
  maxTocLevel: 4,
  minTocLevel: 4,
  source: 'error',
  description: 'Guide to understanding and troubleshooting errors returned when Plaid could not connect to the Item or cannot use the Item in a particular way.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const ErrorReference = makeShortcode("ErrorReference");
const Checkbox = makeShortcode("Checkbox");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Item errors' subtitle='Guide to troubleshooting Item errors' mdxType="Header" />
    <h4 {...{
      "id": "access_not_granted"
    }}><strong parentName="h4">{`ACCESS_NOT_GRANTED`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='ACCESS_NOT_GRANTED' subtitle="The user did not grant necessary permissions for their account." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`This Item's access is affected by institution-hosted access controls.`}</li>
        <li parentName="ul">{`The user did not agree to share, or has revoked, access to the data required for the requested product. Note that for some institutions, the end user may need to specifically opt-in during the OAuth flow to share specific details, such as identity data, or account and routing number information, even if they have already opted in to sharing information about a specific account.`}</li>
        <li parentName="ul">{`The user does not have permission to share required information about the account. This can happen at some institutions using OAuth connections if the user is not the account owner (for example, they have a role such as trustee, investment advisor, power of attorney holder, or authorized cardholder).`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox mdxType="Checkbox">
        <p>{`Prompt the end user to allow Plaid to access identity data and/or account and routing number data. The end user should do this during the Link flow if they were unable to successfully complete the Link flow for the Item, or via Link's `}<a parentName="p" {...{
            "href": "/docs/link/update-mode"
          }}>{`update mode`}</a>{` if the Item has already been added.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`If you frequently experience this error, you may want to ask your account manager to enable `}<a parentName="p" {...{
            "href": "/docs/link/update-mode/#resolving-access_not_granted-or-no_auth_accounts-errors-via-product-validations"
          }}>{`update mode with Product Validations (UMPV)`}</a>{` for your account. Sending the end user through UMPV will ensure that they provide the correct permissions during the OAuth flow.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`If your Plaid integration involves adding products to Items after Link (instead of specifying these products in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` `}<inlineCode parentName="p">{`products`}</inlineCode>{` array) consider using `}<a parentName="p" {...{
            "href": "/docs/link/initializing-products/#required-if-supported-products"
          }}>{`Required if Supported Products`}</a>{` instead. This will ensure that your user has selected the required permissions for these additional products during Link, reducing the frequency with which `}<inlineCode parentName="p">{`ACCESS_NOT_GRANTED`}</inlineCode>{` errors will occur.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`If there are other security settings on the user's account that prevent sharing data with aggregators, they should adjust their security preferences on their institution's online banking portal. It may take up to 48 hours for changes to take effect.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`Confirm that the user is the owner of the account.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "instant_match_failed"
    }}><strong parentName="h4">{`INSTANT_MATCH_FAILED`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='INSTANT_MATCH_FAILED' subtitle="Instant Match could not be initialized for the Item." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-1"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`The Item does not support Instant Auth or Instant Match. If this is the case, Plaid will automatically attempt to enter a micro-deposit based verification flow.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-1"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox mdxType="Checkbox">
        <p>{`Review Link activity logs to verify whether a micro-deposit verification flow was launched in Link after this error occurred. If it was not launched, see `}<a parentName="p" {...{
            "href": "/docs/auth/coverage/"
          }}>{`Add institution coverage`}</a>{` for more information on enabling micro-deposit verification flows. If it was launched successfully, no further troubleshooting action is required.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "insufficient_credentials"
    }}><strong parentName="h4">{`INSUFFICIENT_CREDENTIALS`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='INSUFFICIENT_CREDENTIALS' subtitle="The user did not provide sufficient authorization in order to link their account via an OAuth login flow." client server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-2"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Your user abandoned the bank OAuth flow without completing it.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-2"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='INSUFFICIENT_CREDENTIALS-1' mdxType="Checkbox">
        <p>{`Have your user attempt to link their account again.`}</p>
      </Checkbox>
      <p>{`If this error persists, please submit a
`}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/support/new"
        }}>{`Support`}</a>{` ticket. Which identifiers are
useful varies by issue, but commonly include `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, `}<inlineCode parentName="p">{`institution_id`}</inlineCode>{`,
and either `}<inlineCode parentName="p">{`link_session_id`}</inlineCode>{` or `}<inlineCode parentName="p">{`request_id`}</inlineCode>{`.`}</p>
    </ErrorReference>
    <h4 {...{
      "id": "invalid_credentials"
    }}><strong parentName="h4">{`INVALID_CREDENTIALS`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='INVALID_CREDENTIALS' subtitle="The financial institution indicated that the credentials provided were invalid." client mdxType="ErrorReference">
      <h5 {...{
        "id": "link-user-experience"
      }}>{`Link user experience`}</h5>
      <p>{`Your user will be redirected to the `}<inlineCode parentName="p">{`Credentials`}</inlineCode>{` pane to retry entering correct credentials.`}</p>
      <h5 {...{
        "id": "common-causes-3"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`The user entered incorrect credentials at their selected institution.`}
          <ul parentName="li">
            <li parentName="ul">{`Extra spaces, capitalization, and punctuation errors are common causes of `}<inlineCode parentName="li">{`INVALID_CREDENTIALS`}</inlineCode>{`.`}</li>
          </ul>
        </li>
        <li parentName="ul">{`The institution requires special configuration steps before the user can link their account with Plaid. KeyBank, Interactive Brokers, Morgan Stanley, and Betterment are examples of institutions that require this setup.`}</li>
        <li parentName="ul">{`The user selected the wrong institution.`}
          <ul parentName="li">
            <li parentName="ul">{`Plaid supports institutions that have multiple login portals for the various products they offer, and it is common for users to confuse a different selection for the one which their credentials would actually be accepted.`}</li>
            <li parentName="ul">{`This confusion is particularly common between Vanguard (brokerage accounts) and My Vanguard Plan (retirement accounts). This is also common for users attempting to link prepaid stored-value cards, as many institutions have separate login portals specifically for those cards.`}</li>
          </ul>
        </li>
        <li parentName="ul">{`The institution is experiencing a temporary interruption or connectivity issue, which can cause it to reject valid credentials.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-3"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='INVALID_CREDENTIALS-4' mdxType="Checkbox">
        <p>{`Check the status of the institution via the `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/activity/status"
          }}>{`Dashboard`}</a>{` or `}<a parentName="p" {...{
            "href": "/docs/api/institutions"
          }}>{`Institutions API`}</a>{`. If the institution is showing a known outage or degraded connectivity, the error is likely due to the interruption rather than the user's credentials.`}</p>
      </Checkbox>
      <Checkbox checklistId='INVALID_CREDENTIALS-1' mdxType="Checkbox">
        <p>{`Prompt your user to retry entering their credentials.`}</p>
      </Checkbox>
      <p>{`Note: the institution may lock a user out of their account after 3-5 repeated attempts, resulting in an `}<a parentName="p" {...{
          "href": "#item_locked"
        }}><inlineCode parentName="a">{`ITEM_LOCKED`}</inlineCode></a>{` error.`}</p>
      <Checkbox checklistId='INVALID_CREDENTIALS-5' mdxType="Checkbox">
        <p>{`Confirm that the credentials being entered are correct by asking the user to test logging in to their financial institution website using the same set of credentials.`}</p>
      </Checkbox>
      <Checkbox checklistId="INVALID_CREDENTIALS-3" mdxType="Checkbox">
        <p>{`The user should check their financial institution website for special settings to allow access for third-party apps, such as a "third party application password" or "allow third party access" setting.`}</p>
      </Checkbox>
      <Checkbox checklistId='INVALID_CREDENTIALS-2' mdxType="Checkbox">
        <p>{`Verify that the user is selecting the correct institution in Link's Institution Select view.`}</p>
      </Checkbox>
      <p>{`If the credentials are confirmed to be legitimate, and a user still cannot authenticate, please submit a support ticket via the `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/support/new/financial-institutions/authentication-issues/invalid-creds"
        }}>{`Dashboard`}</a>{`. Which identifiers are useful varies by issue, but commonly include `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, `}<inlineCode parentName="p">{`institution_id`}</inlineCode>{`, and either `}<inlineCode parentName="p">{`link_session_id`}</inlineCode>{` or `}<inlineCode parentName="p">{`request_id`}</inlineCode>{`.`}</p>
    </ErrorReference>
    <h4 {...{
      "id": "invalid_mfa"
    }}><strong parentName="h4">{`INVALID_MFA`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='INVALID_MFA' subtitle="The institution indicated that the provided response for MFA was invalid" client mdxType="ErrorReference">
      <h5 {...{
        "id": "link-user-experience-1"
      }}>{`Link user experience`}</h5>
      <p>{`Your user will be redirected to the MFA pane to retry entering the correct value.`}</p>
      <h5 {...{
        "id": "common-causes-4"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`The user entered an incorrect answer for the security question presented by the selected institution.`}</li>
        <li parentName="ul">{`The user selected an MFA device that is not active.`}</li>
        <li parentName="ul">{`The institution failed to send the one-time code for the user's selected device.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-4"
      }}>{`Troubleshooting steps`}</h5>
      <p>{`If the user still cannot log in despite providing correct information, or if they cannot receive an MFA token despite having the correct device, please submit a support ticket via the `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/support/new/financial-institutions/authentication-issues/invalid-creds"
        }}>{`Dashboard`}</a>{`. Which identifiers are useful varies by issue, but commonly include `}<inlineCode parentName="p">{`institution_id`}</inlineCode>{`, and either `}<inlineCode parentName="p">{`link_session_id`}</inlineCode>{` or `}<inlineCode parentName="p">{`request_id`}</inlineCode>{`.`}</p>
    </ErrorReference>
    <h4 {...{
      "id": "invalid_send_method"
    }}><strong parentName="h4">{`INVALID_SEND_METHOD`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='INVALID_SEND_METHOD' subtitle="Returned when the method used to send MFA credentials was deemed invalid by the institution." client mdxType="ErrorReference">
      <h5 {...{
        "id": "link-user-experience-2"
      }}>{`Link user experience`}</h5>
      <p>{`Your user will be shown an error message indicating that an internal error occurred and will be prompted to close Link.`}</p>
      <h5 {...{
        "id": "common-causes-5"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`The institution is experiencing login issues.`}</li>
        <li parentName="ul">{`The integration between Plaid and the financial institution is experiencing errors.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-5"
      }}>{`Troubleshooting steps`}</h5>
      <p>{`If the error persists, submit a support ticket via the `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/support/new/financial-institutions/authentication-issues/mfa-issue"
        }}>{`Dashboard`}</a>{`. Which identifiers are useful varies by issue, but commonly include `}<inlineCode parentName="p">{`institution_id`}</inlineCode>{` and either `}<inlineCode parentName="p">{`link_session_id`}</inlineCode>{` or `}<inlineCode parentName="p">{`request_id`}</inlineCode>{`.`}</p>
    </ErrorReference>
    <h4 {...{
      "id": "invalid_phone_number"
    }}><strong parentName="h4">{`INVALID_PHONE_NUMBER`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='INVALID_PHONE_NUMBER' subtitle="The submitted phone number was invalid." client mdxType="ErrorReference">
      <h5 {...{
        "id": "link-user-experience-3"
      }}>{`Link user experience`}</h5>
      <p>{`Your user will be redirected to the phone number input pane to retry submitting a valid phone number.`}</p>
      <h5 {...{
        "id": "common-causes-6"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`The user entered a malformed phone number. Note that entering a phone number from a country that the `}<a parentName="li" {...{
            "href": "/docs/link/returning-user/"
          }}>{`returning user experience`}</a>{` does not support does not cause this error; those users are redirected to the standard Link flow instead.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-6"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='INVALID_PHONE_NUMBER-1' mdxType="Checkbox">
        <p>{`If the user still cannot log in despite providing correct information, instruct the user to select "Maybe later" to continue in Link without the returning user experience.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "invalid_otp"
    }}><strong parentName="h4">{`INVALID_OTP`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='INVALID_OTP' subtitle="The submitted OTP was invalid." client mdxType="ErrorReference">
      <h5 {...{
        "id": "link-user-experience-4"
      }}>{`Link user experience`}</h5>
      <p>{`Your user will be redirected to the OTP input pane to retry submitting a valid OTP. After three `}<inlineCode parentName="p">{`INVALID_OTP`}</inlineCode>{` errors, Link will stop accepting OTP inputs and the user will be prompted to exit Link or select another institution. If the flow was the Layer or Returning User Experience (Remember Me) flow, after the third invalid OTP code, instead of `}<inlineCode parentName="p">{`INVALID_OTP`}</inlineCode>{`, the `}<inlineCode parentName="p">{`PROFILE_AUTHENTICATION_FAILED`}</inlineCode>{` error will trigger, and Link will stop accepting OTP inputs.`}</p>
      <h5 {...{
        "id": "common-causes-7"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`The user entered an invalid OTP.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-7"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='INVALID_OTP-1' mdxType="Checkbox">
        <p>{`If the user still cannot log in despite providing correct information, instruct the user to select "Maybe later" to continue in Link without the returning user experience.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "invalid_updated_username"
    }}><strong parentName="h4">{`INVALID_UPDATED_USERNAME`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='INVALID_UPDATED_USERNAME' subtitle="The username entered during update mode did not match the original username." client mdxType="ErrorReference">
      <h5 {...{
        "id": "link-user-experience-5"
      }}>{`Link user experience`}</h5>
      <p>{`Your user will be directed to enter a different username.`}</p>
      <h5 {...{
        "id": "common-causes-8"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`While updating an Item in `}<a parentName="li" {...{
            "href": "/docs/link/update-mode"
          }}>{`update mode`}</a>{`, the user provided a username that doesn't match the original username provided when they originally linked the account.`}</li>
        <li parentName="ul">{`The user was updating an Item in update mode, but Plaid cannot verify that the new Item is the same as the original Item. This may be due to changes to the user's selections during the OAuth UI flow, changes in the `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{` call parameters, or internal system changes at the financial institution.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-8"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='UPDATE_MODE-1' mdxType="Checkbox">
        <p>{`If your user entered the wrong username, or the username for a different account, they should enter the correct, original username.`}</p>
      </Checkbox>
      <Checkbox checklistId='UPDATE_MODE-2' mdxType="Checkbox">
        <p>{`Have your user ensure that the capitalization for their username is the same as it was when they first logged in to Plaid. The username entered during update mode must case-match with the original username, even if the institution does not consider usernames case-sensitive.`}</p>
      </Checkbox>
      <Checkbox checklistId='UPDATE_MODE-3' mdxType="Checkbox">
        <p>{`If your user actually changed the username for the account, you should delete the original Item and direct your user to the regular Link flow to link their account as a new Item.`}</p>
      </Checkbox>
      <Checkbox checklistId='UPDATE_MODE-4' mdxType="Checkbox">
        <p>{`If your user is using the correct and original login credentials, delete the original Item and direct your user to the regular Link flow to link their account as a new Item.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "item_concurrently_deleted"
    }}><strong parentName="h4">{`ITEM_CONCURRENTLY_DELETED`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='ITEM_CONCURRENTLY_DELETED' subtitle="This item was deleted while the operation was in progress." mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-9"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`An Item was deleted via `}<inlineCode parentName="li">{`/item/remove`}</inlineCode>{` while a request for its data was in process.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-9"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox mdxType="Checkbox">
        <p>{`If you plan to delete an Item immediately after retrieving data from it, make sure to wait until your request has successfully returned the data you need before calling `}<inlineCode parentName="p">{`/item/remove`}</inlineCode>{`.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "item_locked"
    }}><strong parentName="h4">{`ITEM_LOCKED`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='ITEM_LOCKED' subtitle="The financial institution indicated that the user's account is locked. The user will need to work with the institution directly to unlock their account." client mdxType="ErrorReference">
      <h5 {...{
        "id": "link-user-experience-6"
      }}>{`Link user experience`}</h5>
      <p>{`Your user will be directed to a new window with the institution's homepage to unlock their account. Link will then display the Institution Select pane for the user to connect a different account.`}</p>
      <h5 {...{
        "id": "common-causes-10"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`The user entered their credentials incorrectly after more than 3-5 attempts, triggering the institution's fraud protection systems and locking the user's account.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-10"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='ITEM_LOCKED-1' mdxType="Checkbox">
        <p>{`Request that the user log in directly to their institution. Attempting to log in is a reliable way of confirming whether the user's account is legitimately locked with a given institution. If the user cannot log in due to their account being locked, the website will usually note it as such, giving supplemental information on what they can do to resolve their account.`}</p>
      </Checkbox>
      <Checkbox checklistId='ITEM_LOCKED-2' mdxType="Checkbox">
        <p>{`If the account is locked, ask the user to work with the financial institution to unlock their account.`}</p>
        <p>{`Steps on unlocking an account are usually provided when a login attempt fails with the institution directly, in the event that their account is actually locked. Once unlocked, they should then try to re-authenticate using Plaid Link.`}</p>
      </Checkbox>
      <p>{`If the user is persistently locked out of their institution, submit a support ticket via the `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/support/new/financial-institutions/authentication-issues/invalid-creds"
        }}>{`Dashboard`}</a>{`. Which identifiers are useful varies by issue, but commonly include `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, `}<inlineCode parentName="p">{`institution_id`}</inlineCode>{`, and either `}<inlineCode parentName="p">{`link_session_id`}</inlineCode>{` or `}<inlineCode parentName="p">{`request_id`}</inlineCode>{`.`}</p>
    </ErrorReference>
    <h4 {...{
      "id": "item_login_required"
    }}><strong parentName="h4">{`ITEM_LOGIN_REQUIRED`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='ITEM_LOGIN_REQUIRED' subtitle="Additional input from the user is required to continue getting data for this Item." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-11"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`The institution does not use an OAuth-based connection and the user changed their password.`}</li>
        <li parentName="ul">{`The institution does not use an OAuth-based connection and the user changed their multi-factor settings, or their multi-factor authentication has expired.`}</li>
        <li parentName="ul">{`The institution has undergone a migration from a non-OAuth-based connection to an OAuth-based connection.`}</li>
        <li parentName="ul">{`The institution uses an OAuth-based connection and the user's consent is no longer valid, either because it has expired, or because the user revoked access.`}</li>
        <li parentName="ul">{`The institution uses an OAuth-based connection that does not allow duplicate Items, but the user connected a duplicate Item to the same application.`}</li>
        <li parentName="ul">{`(Sandbox only) The Item was created in the Sandbox environment and is over 30 days old.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-11"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox mdxType="Checkbox">
        <p>{`Send the Item through Link's `}<a parentName="p" {...{
            "href": "/docs/link/update-mode"
          }}>{`update mode`}</a>{`. Update mode will then automatically prompt the user for whatever input (such as a new password, or refreshed consent) is necessary to fix the Item.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`If the Item is on an OAuth-based connection and has an expired `}<inlineCode parentName="p">{`consent_expiration_time`}</inlineCode>{`, you may be able to reduce the frequency of this error by listening for the `}<a parentName="p" {...{
            "href": "/docs/api/items/#pending_disconnect"
          }}><inlineCode parentName="a">{`PENDING_DISCONNECT`}</inlineCode></a>{` webhook (US/CA Items) or the `}<a parentName="p" {...{
            "href": "/docs/api/items/#pending_expiration"
          }}><inlineCode parentName="a">{`PENDING_EXPIRATION`}</inlineCode></a>{` webhook (UK/EU Items) and proactively sending the user through update mode before the Item expires.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`If the Item is on an OAuth-based connection and the error was caused by the user adding a duplicate Item, remove the old Item, and see `}<a parentName="p" {...{
            "href": "/docs/link/duplicate-items/"
          }}>{`preventing duplicate Items`}</a>{` for more details on preventing and remediating duplicate Items.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`To recover from this error more quickly, listen for the `}<a parentName="p" {...{
            "href": "/docs/api/items/#login_repaired"
          }}><inlineCode parentName="a">{`LOGIN_REPAIRED`}</inlineCode></a>{` webhook, which will fire when an Item exits `}<inlineCode parentName="p">{`ITEM_LOGIN_REQUIRED`}</inlineCode>{` without going through update mode in your app.`}</p>
      </Checkbox>
      <p>{`If the error is legitimate, having the user authenticate again should generally return their Item to a healthy state without further intervention.`}</p>
      <p>{`If this error persists, please submit a `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/support/new/financial-institutions/authentication-issues"
        }}>{`Support`}</a>{` ticket. Which identifiers are useful varies by issue, but commonly include `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, `}<inlineCode parentName="p">{`institution_id`}</inlineCode>{`, and either `}<inlineCode parentName="p">{`link_session_id`}</inlineCode>{` or `}<inlineCode parentName="p">{`request_id`}</inlineCode>{`.`}</p>
    </ErrorReference>
    <h4 {...{
      "id": "item_not_found"
    }}><strong parentName="h4">{`ITEM_NOT_FOUND`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='ITEM_NOT_FOUND' subtitle='The Item you requested cannot be found. This Item does not exist, has been previously removed via /item/remove, or has had access removed by the user' server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-12"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Item was previously removed via `}<inlineCode parentName="li">{`/item/remove`}</inlineCode>{`.`}</li>
        <li parentName="ul">{`The user has depermissioned or deleted their Item via `}<a parentName="li" {...{
            "href": "https://my.plaid.com"
          }}>{`my.plaid.com`}</a>{`.`}</li>
        <li parentName="ul">{`Plaid support has deleted the Item in response to a data deletion request from the user.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-12"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox mdxType="Checkbox">
        <p>{`Launch a new instance of Plaid Link and prompt the user to create a new Item.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "item_not_supported"
    }}><strong parentName="h4">{`ITEM_NOT_SUPPORTED`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='ITEM_NOT_SUPPORTED' subtitle="Plaid is unable to support this user's accounts due to restrictions in place at the financial institution." client mdxType="ErrorReference">
      <h5 {...{
        "id": "link-user-experience-7"
      }}>{`Link user experience`}</h5>
      <p>{`Your user will be redirected to the Institution Select pane to connect a different account.`}</p>
      <h5 {...{
        "id": "common-causes-13"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Plaid does not currently support the types of accounts for the connected Item, due to restrictions in place at the selected institution.`}</li>
        <li parentName="ul">{`Plaid does not currently support the specific type of multi-factor authentication in place at the selected institution.`}</li>
        <li parentName="ul">{`The credentials provided are for a 'guest' account or other account type with limited account access.`}</li>
        <li parentName="ul">{`A processor partner token is being created or used, but the country, products, or account types associated with the Item are not supported by the processor partner. `}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-13"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='ITEM_NOT_SUPPORTED-2' mdxType="Checkbox">
        <p>{`If the item was previously healthy and this error only began after calling a product endpoint on the Item, send the Item through `}<a parentName="p" {...{
            "href": "/docs/link/update-mode"
          }}>{`update mode`}</a>{`. `}</p>
      </Checkbox>
      <Checkbox checklistId='ITEM_NOT_SUPPORTED-1' mdxType="Checkbox">
        <p>{`Prompt the user to connect a different account and institution.`}</p>
      </Checkbox>
      <p>{`If the error persists and none of the common causes above seem to apply, `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/support/new/"
        }}>{`contact support`}</a>{`.`}</p>
    </ErrorReference>
    <h4 {...{
      "id": "manual_verification_required"
    }}><strong parentName="h4">{`MANUAL_VERIFICATION_REQUIRED`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='MANUAL_VERIFICATION_REQUIRED' subtitle="Returned when a data request has been made for a product that requires the user to manually verify their Item via Link's update mode." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-14"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul"><inlineCode parentName="li">{`/investments/auth/get`}</inlineCode>{` was called on an Item that could not be automatically verified. This can occur when the institution only supports verification through one of the `}<a parentName="li" {...{
            "href": "/docs/investments-move/#fallback-flows"
          }}>{`Investments Move fallback flows`}</a>{`.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-14"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox mdxType="Checkbox">
        <p>{`(If using Investments Move) Verify the Item manually by having the user go through a `}<a parentName="p" {...{
            "href": "/docs/link/update-mode/#requesting-additional-products-accounts-permissions-or-use-cases"
          }}>{`Link update mode`}</a>{` session using the existing access token and with `}<inlineCode parentName="p">{`investments_auth`}</inlineCode>{` initialized in the `}<inlineCode parentName="p">{`products`}</inlineCode>{` array.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "mfa_not_supported"
    }}><strong parentName="h4">{`MFA_NOT_SUPPORTED`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='MFA_NOT_SUPPORTED' subtitle="Returned when the user requires a form of MFA that Plaid does not support for a given financial institution." client mdxType="ErrorReference">
      <h5 {...{
        "id": "link-user-experience-8"
      }}>{`Link user experience`}</h5>
      <p>{`Your user will be redirected to the Institution Select pane to connect a different account.`}</p>
      <h5 {...{
        "id": "common-causes-15"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Plaid does not currently support the specific type of multi-factor authentication in place at the selected institution.`}</li>
        <li parentName="ul">{`The user's multi-factor authentication setting is configured not to remember trusted devices and instead to present a multi-factor challenge on every login attempt. This prevents Plaid from refreshing data asynchronously, which many products (especially Transactions) require.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-15"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='MFA_NOT_SUPPORTED-1' mdxType="Checkbox">
        <p>{`Prompt the user to connect a different account and institution.`}</p>
      </Checkbox>
      <Checkbox checklistId='MFA_NOT_SUPPORTED-2' mdxType="Checkbox">
        <p>{`If your application does not require asynchronous data refresh to work properly, `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/support/new/"
          }}>{`contact support`}</a>{` to explore your options for enabling user access.`}</p>
      </Checkbox>
      <p>{`If the user's settings are not configured to present a multi-factor challenge on every login attempt but this error is still appearing, submit a support ticket via the `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/support/new/financial-institutions/authentication-issues/mfa-issue"
        }}>{`Dashboard`}</a>{`. Which identifiers are useful varies by issue, but commonly include `}<inlineCode parentName="p">{`institution_id`}</inlineCode>{` and either `}<inlineCode parentName="p">{`link_session_id`}</inlineCode>{` or `}<inlineCode parentName="p">{`request_id`}</inlineCode>{`.`}</p>
    </ErrorReference>
    <h4 {...{
      "id": "no_accounts"
    }}><strong parentName="h4">{`NO_ACCOUNTS`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='NO_ACCOUNTS' subtitle="Returned when there are no open accounts associated with the Item." client mdxType="ErrorReference">
      <h5 {...{
        "id": "link-user-experience-9"
      }}>{`Link user experience`}</h5>
      <p>{`Your user will be redirected to the Institution Select pane to connect a different account.`}</p>
      <h5 {...{
        "id": "common-causes-16"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`The user successfully logged into their account, but Plaid was unable to retrieve any open, active, or eligible accounts in the connected Item.`}</li>
        <li parentName="ul">{`The user closed their account.`}</li>
        <li parentName="ul">{`The user revoked access to the account.`}</li>
        <li parentName="ul">{`The account experienced `}<a parentName="li" {...{
            "href": "/docs/errors/invalid-input/#account-churn"
          }}>{`account churn`}</a>{`, which happens when Plaid can no longer recognize an account as the same one that the user granted you access to. When this happens, your permissions to the account may be revoked.`}</li>
        <li parentName="ul">{`There is a problem with Plaid's connection to the user's financial institution.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-16"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='NO_ACCOUNTS-0' mdxType="Checkbox">
        <p>{`If you are using the `}<inlineCode parentName="p">{`account_filters`}</inlineCode>{` parameter when calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`, ensure that you are not filtering out valid accounts that could be used with your app.`}</p>
      </Checkbox>
      <Checkbox checklistId='NO_ACCOUNTS-1' mdxType="Checkbox">
        <p>{`If the Item is at an institution that uses OAuth, ensure that the user has not denied or revoked OAuth access to all of their eligible accounts.`}</p>
      </Checkbox>
      <Checkbox checklistId='NO_ACCOUNTS-5' mdxType="Checkbox">
        <p>{`If you previously had access to the account, send your user through `}<a parentName="p" {...{
            "href": "/docs/link/update-mode/#using-update-mode-to-request-new-accounts"
          }}>{`update mode with account selection enabled`}</a>{` to allow them to re-grant access to the account.`}</p>
      </Checkbox>
      <Checkbox checklistId='NO_ACCOUNTS-3' mdxType="Checkbox">
        <p>{`Ensure the user has not closed their account. If the user no longer has any open accounts associated with the Item, remove the Item via `}<inlineCode parentName="p">{`/item/remove`}</inlineCode>{`.`}</p>
      </Checkbox>
      <Checkbox checklistId='NO_ACCOUNTS-2' mdxType="Checkbox">
        <p>{`Prompt the user to connect a different account and institution.`}</p>
      </Checkbox>
      <p>{`If this error persists and the user does have accounts at the institution that you should be able to access, please submit a `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/support/new"
        }}>{`Support`}</a>{` ticket. Which identifiers are useful varies by issue, but commonly include `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, `}<inlineCode parentName="p">{`institution_id`}</inlineCode>{`, and either `}<inlineCode parentName="p">{`link_session_id`}</inlineCode>{` or `}<inlineCode parentName="p">{`request_id`}</inlineCode>{`.`}</p>
    </ErrorReference>
    <h4 {...{
      "id": "no_auth_accounts-or-no-depository-accounts"
    }}><strong parentName="h4">{`NO_AUTH_ACCOUNTS or no-depository-accounts`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='NO_AUTH_ACCOUNTS' subtitle="Returned from POST /auth/get when there are no valid accounts for which account and routing numbers could be retrieved." server client mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-17"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul"><inlineCode parentName="li">{`/auth/get`}</inlineCode>{` was called on an Item with no accounts that can support Auth, or accounts that do support Auth were filtered out. Only debitable checking, savings, and cash management accounts can be used with Auth. `}</li>
        <li parentName="ul">{`Plaid's ability to retrieve Auth data from the institution has been temporarily disrupted.`}</li>
        <li parentName="ul">{`The end user is connecting to an institution that uses OAuth-based flows but did not grant permission in the OAuth flow for the institution to share details for any compatible accounts. Note that for some institutions, the end user may need to specifically opt-in during the OAuth flow to share account and routing number information even if they have already opted in to sharing information about their checking or savings account.`}</li>
        <li parentName="ul">{`The end user revoked access to the account.`}</li>
        <li parentName="ul">{`The account experienced `}<a parentName="li" {...{
            "href": "/docs/errors/invalid-input/#account-churn"
          }}>{`account churn`}</a>{`, which happens when Plaid can no longer recognize an account as the same one that the user granted you access to. When this happens, your permissions to the account may be revoked.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-17"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox mdxType="Checkbox">
        <p>{`Ensure that any `}<inlineCode parentName="p">{`account_id`}</inlineCode>{` specified in the `}<inlineCode parentName="p">{`options`}</inlineCode>{` filter for `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{` belongs to a debitable checking, savings, or cash management account.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`Ensure that the end user has a debitable checking, savings, or cash management account at the institution. Not all accounts permit ACH debits. Common examples of non-debitable accounts include savings accounts at Chime or at Navy Federal Credit Union (NFCU).`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`If the error occurred after attempting to add Auth to a previously healthy Item, send the user through `}<a parentName="p" {...{
            "href": "/docs/link/update-mode/#resolving-access_not_granted-or-no_auth_accounts-errors-via-product-validations"
          }}>{`update mode with Product Validations`}</a>{`.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`If the Item is at an OAuth-based institution, prompt the end user to allow Plaid to access a debitable checking, savings, or cash management account, along with its account and routing number data. The end user should do this during the Link flow if they were unable to successfully complete the Link flow for the Item, or at their institution's online banking portal if the Item has already been added.`}</p>
      </Checkbox>
      <Checkbox checklistId="NO_AUTH_ACCOUNTS_1" mdxType="Checkbox">
        <p>{`If you previously had access to the account, send your user through the `}<a parentName="p" {...{
            "href": "/docs/link/update-mode"
          }}>{`update mode flow`}</a>{` to allow them to re-grant access to the account.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`Return your user to the Link flow and prompt them to link a different account.`}</p>
      </Checkbox>
      <p>{`If the problem persists even though the end user has confirmed that they have a debitable checking, savings, or cash management account at the institution, `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/support/new"
        }}>{`open a support ticket`}</a>{`.`}</p>
    </ErrorReference>
    <h4 {...{
      "id": "no_eligible_items"
    }}><strong parentName="h4">{`NO_ELIGIBLE_ITEMS`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='NO_ELIGIBLE_ITEMS' subtitle='Returned by the legacy Cash Flow Updates endpoints (`/cra/monitoring_insights/*`) when the user associated with the provided `user_token` has no Items that are eligible for Cash Flow Updates.' server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-18"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`The user has no linked Items, or every Item they had linked has since been removed.`}</li>
        <li parentName="ul">{`None of the user's Items is eligible for Cash Flow Updates. For example, the Item's institution does not support the transaction history the report requires.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-18"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='NO_ELIGIBLE_ITEMS' mdxType="Checkbox">
        <p>{`Call `}<inlineCode parentName="p">{`/user/items/get`}</inlineCode>{` to confirm the user has at least one Item, and check each Item's status for errors.`}</p>
      </Checkbox>
      <Checkbox checklistId='NO_ELIGIBLE_ITEMS_2' mdxType="Checkbox">
        <p>{`If an Item is in an error state, send the user through Link in `}<a parentName="p" {...{
            "href": "/docs/link/update-mode"
          }}>{`update mode`}</a>{` to restore the connection. If the user has no Items, send them through Link to add one.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "no_investment_accounts"
    }}><strong parentName="h4">{`NO_INVESTMENT_ACCOUNTS`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='NO_INVESTMENT_ACCOUNTS' subtitle="Returned from POST /investments/holdings/get, POST /investments/transactions/get, or Link initialized with the Investments product, when there are no valid investment account(s) for which holdings or transactions could be retrieved." server client mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-19"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Link was initialized with the Investments product, but the user attempted to link an account with no investment accounts.`}</li>
        <li parentName="ul">{`The `}<inlineCode parentName="li">{`/investments/holdings/get`}</inlineCode>{` or `}<inlineCode parentName="li">{`/investments/transactions/get`}</inlineCode>{` endpoint was called, but there are no investment accounts associated with the Item.`}</li>
        <li parentName="ul">{`The end user is connecting to an institution that uses OAuth-based flows but did not grant permission in the OAuth flow for the institution to share details for any investment accounts.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-19"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='NO_INVESTMENT_ACCOUNTS-1' mdxType="Checkbox">
        <p>{`Have the user open an investment account at the institution and then re-link, or link an Item that already has an investment account.`}</p>
      </Checkbox>
      <Checkbox checklistId='NO_INVESTMENT_ACCOUNTS-2' mdxType="Checkbox">
        <p>{`If your end user is connecting to an institution that uses OAuth-based flows (one for which the `}<inlineCode parentName="p">{`oauth`}</inlineCode>{` field in the institution record is `}<inlineCode parentName="p">{`true`}</inlineCode>{`), ensure that your end user consented to share details for an investment account.`}</p>
      </Checkbox>
      <p>{`If the problem persists, Plaid may be erroneously categorizing the account. If this is the case, `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/support/new"
        }}>{`open a support ticket`}</a>{`.`}</p>
    </ErrorReference>
    <h4 {...{
      "id": "no_liability_accounts"
    }}><strong parentName="h4">{`NO_LIABILITY_ACCOUNTS`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='NO_LIABILITY_ACCOUNTS' subtitle="Returned from POST /liabilities/get when there are no valid liability account(s) for which liabilities could be retrieved." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-20"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`The `}<inlineCode parentName="li">{`/liabilities/get`}</inlineCode>{` endpoint was called, but there are no supported liability accounts associated with the Item.`}</li>
        <li parentName="ul">{`The end user is connecting to an institution that uses OAuth-based flows but did not grant permission in the OAuth flow for the institution to share details for any liabilities accounts.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-20"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox mdxType="Checkbox">
        <p>{`Make sure the user has linked an Item with a supported account type and subtype. Liabilities supports `}<inlineCode parentName="p">{`credit`}</inlineCode>{` accounts with the subtypes `}<inlineCode parentName="p">{`credit card`}</inlineCode>{` or `}<inlineCode parentName="p">{`paypal`}</inlineCode>{`, and `}<inlineCode parentName="p">{`loan`}</inlineCode>{` accounts with the subtypes `}<inlineCode parentName="p">{`auto`}</inlineCode>{`, `}<inlineCode parentName="p">{`commercial`}</inlineCode>{`, `}<inlineCode parentName="p">{`commercial line of credit`}</inlineCode>{`, `}<inlineCode parentName="p">{`consumer`}</inlineCode>{`, `}<inlineCode parentName="p">{`home equity`}</inlineCode>{`, `}<inlineCode parentName="p">{`home equity loan`}</inlineCode>{`, `}<inlineCode parentName="p">{`line of credit`}</inlineCode>{`, `}<inlineCode parentName="p">{`loan`}</inlineCode>{`, `}<inlineCode parentName="p">{`mortgage`}</inlineCode>{`, and `}<inlineCode parentName="p">{`student`}</inlineCode>{`.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`If your end user is connecting to an institution that uses OAuth-based flows (one for which the `}<inlineCode parentName="p">{`oauth`}</inlineCode>{` field in the institution record is `}<inlineCode parentName="p">{`true`}</inlineCode>{`), ensure that your end user consented to share details for a supported credit or loan account.`}</p>
      </Checkbox>
      <p>{`If the problem persists, Plaid may be erroneously categorizing the account. If this is the case, `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/support/new"
        }}>{`open a support ticket`}</a>{`.`}</p>
    </ErrorReference>
    <h4 {...{
      "id": "password_reset_required"
    }}><strong parentName="h4">{`PASSWORD_RESET_REQUIRED`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='PASSWORD_RESET_REQUIRED' subtitle="The user must log in directly to the financial institution and reset their password." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-21"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`The institution is blocking access because the user needs to reset their password.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-21"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='PASSWORD_RESET_REQUIRED' mdxType="Checkbox">
        <p>{`Request that the user log in and reset their password directly at their institution.`}</p>
      </Checkbox>
      <p>{`Ask that the user log in to the bank, and confirm whether they are presented with a message to reset their password.
If they are, they should follow the instructions to reset their password.
Once the password is reset, they should attempt to link their account again.`}</p>
    </ErrorReference>
    <h4 {...{
      "id": "product_not_enabled"
    }}><strong parentName="h4">{`PRODUCT_NOT_ENABLED`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='PRODUCT_NOT_ENABLED' subtitle="A requested product was not enabled for the current access token. Please ensure it is included when initializing Link and create the Item again." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-22"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`You requested a product that was not enabled for the current access token. Ensure it is included when calling `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{` and create the Item again.`}</li>
      </ul>
    </ErrorReference>
    <h4 {...{
      "id": "product_not_ready"
    }}><strong parentName="h4">{`PRODUCT_NOT_READY`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='PRODUCT_NOT_READY' subtitle="Returned when a data request has been made for a product that is not yet ready." server mdxType="ErrorReference">
      <Callout mdxType="Callout">
        <p>{`For the Assets version of this error, see `}<a parentName="p" {...{
            "href": "/docs/errors/assets/#product_not_ready"
          }}><inlineCode parentName="a">{`ASSET_REPORT_ERROR: PRODUCT_NOT_READY`}</inlineCode></a>{`. For the Income Verification version, see `}<a parentName="p" {...{
            "href": "/docs/errors/income/#product_not_ready"
          }}><inlineCode parentName="a">{`INCOME_VERIFICATION_ERROR: PRODUCT_NOT_READY`}</inlineCode></a>{`.`}</p>
      </Callout>
      <h5 {...{
        "id": "common-causes-23"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul"><inlineCode parentName="li">{`/transactions/get`}</inlineCode>{` was called before the first 30 days of transactions data could be extracted. This typically happens if the endpoint was called within a few seconds of linking the Item. It will also happen if `}<inlineCode parentName="li">{`/transactions/get`}</inlineCode>{` was called for the first time on an Item that was not initialized with `}<inlineCode parentName="li">{`transactions`}</inlineCode>{` in the `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{` call. Note that this error does not occur when using `}<inlineCode parentName="li">{`/transactions/sync`}</inlineCode>{`; if called too early, `}<inlineCode parentName="li">{`/transactions/sync`}</inlineCode>{` will simply fail to return any transactions.`}</li>
        <li parentName="ul">{`Occasionally, this error can occur upon calling `}<inlineCode parentName="li">{`/transactions/get`}</inlineCode>{` if Plaid's attempt to extract transactions for the Item has failed, due to a connectivity error with the financial institution, and Plaid has never successfully extracted transactions for the Item in the past. If this happens, Plaid will continue to retry the extraction at least once a day.`}</li>
        <li parentName="ul"><inlineCode parentName="li">{`/signal/evaluate`}</inlineCode>{` was called for the first time on a new Item before any balance data could be extracted for the Item. When called, `}<inlineCode parentName="li">{`/signal/evaluate`}</inlineCode>{` will return a `}<inlineCode parentName="li">{`PRODUCT_NOT_READY`}</inlineCode>{` error if cached balance data does not exist and new balance data could not be obtained after approximately 60 seconds. For some customers who participated in the early phases of the Signal beta, `}<inlineCode parentName="li">{`/signal/evaluate`}</inlineCode>{` will return `}<inlineCode parentName="li">{`PRODUCT_NOT_READY`}</inlineCode>{` immediately if cached balance data is not available when `}<inlineCode parentName="li">{`/signal/evaluate`}</inlineCode>{` is called.`}</li>
        <li parentName="ul"><inlineCode parentName="li">{`/auth/get`}</inlineCode>{` was called on an Item that hasn't been verified, which is possible when using `}<a parentName="li" {...{
            "href": "/docs/auth/coverage"
          }}>{`micro-deposit based verification`}</a>{`.`}</li>
        <li parentName="ul"><inlineCode parentName="li">{`/investments/transactions/get`}</inlineCode>{` was called before the investments data could be extracted. This typically happens when the endpoint is called with the option for `}<inlineCode parentName="li">{`async_update`}</inlineCode>{` set to true, and called again within a few seconds of linking the Item. It will also happen if `}<inlineCode parentName="li">{`/investments/transactions/get`}</inlineCode>{` (with `}<inlineCode parentName="li">{`async_update`}</inlineCode>{` set to true) was called for the first time on an Item that was not initialized with `}<inlineCode parentName="li">{`investments`}</inlineCode>{` in the `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{` call.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-22"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox mdxType="Checkbox">
        <p>{`If you know at the point of Link initialization that you will want to use Transactions with the linked Item, initialize Link with `}<inlineCode parentName="p">{`transactions`}</inlineCode>{` in order to start the product initialization process ahead of time. For more details, see `}<a parentName="p" {...{
            "href": "/docs/link/initializing-products/"
          }}>{`Choosing how to initialize products`}</a>{`.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`Listen for the `}<inlineCode parentName="p">{`INITIAL_UPDATE`}</inlineCode>{` webhook fired when the Transactions product is ready and only call `}<inlineCode parentName="p">{`/transactions/get`}</inlineCode>{` after that webhook has been fired. A successful response before the webhook can still contain incomplete transaction enrichment.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`(If using Auth) Verify the Item manually, or wait for automated verification to succeed.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`(For failures to `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`) Wait a few seconds, then retry.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`Listen for the `}<inlineCode parentName="p">{`HISTORICAL_UPDATE`}</inlineCode>{` webhook fired when the Investments product is ready and only call `}<inlineCode parentName="p">{`/investments/transactions/get`}</inlineCode>{` after that webhook has been fired.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "products_not_supported"
    }}><strong parentName="h4">{`PRODUCTS_NOT_SUPPORTED`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='PRODUCTS_NOT_SUPPORTED' subtitle="Returned when a data request has been made for an Item for a product that it does not support. Use the /item/get endpoint to find out which products an Item supports." server mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-24"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`A product endpoint request was made for an Item that does not support that product. This can happen when trying to call a product endpoint on an Item if the product was not included in either the `}<inlineCode parentName="li">{`products`}</inlineCode>{` or `}<inlineCode parentName="li">{`optional_products`}</inlineCode>{` arrays when calling `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{`.`}</li>
        <li parentName="ul">{`Updated accounts have been requested for an Item initialized with the Assets product, which does not support adding or updating accounts after the initial Link.`}</li>
        <li parentName="ul">{`You are attempting to call `}<inlineCode parentName="li">{`/signal/evaluate`}</inlineCode>{` on a non-US financial institution.`}</li>
        <li parentName="ul">{`You are attempting to call `}<inlineCode parentName="li">{`/accounts/balance/get`}</inlineCode>{` on a manually linked Item (Database Insights, Database Match, Instant Micro-deposits, Same-Day Micro-deposits) or on an Item that contains only limited purpose checking accounts. `}</li>
        <li parentName="ul">{`You are attempting to call `}<inlineCode parentName="li">{`/transactions/refresh`}</inlineCode>{` on a Capital One Item that contains only credit cards or other non-depository products. Capital One does not support the `}<inlineCode parentName="li">{`/transactions/refresh`}</inlineCode>{` endpoint for non-depository products.`}</li>
        <li parentName="ul">{`You are attempting to call `}<inlineCode parentName="li">{`/identity/match`}</inlineCode>{` on an Item that was manually linked (using Database Insights, Database Match, Instant Micro-deposits, or Same-Day Micro-deposits) that has not been seen on the Plaid network before.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-23"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox mdxType="Checkbox">
        <p>{`Use the `}<inlineCode parentName="p">{`/item/get`}</inlineCode>{` endpoint to determine which products a given Item supports.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`Use the `}<inlineCode parentName="p">{`/institutions/get_by_id`}</inlineCode>{` endpoint to determine which products a given institution supports. Receiving this error is expected if the user's institution does not support the requested product.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`If the product is critical to your use case, put it in the `}<inlineCode parentName="p">{`products`}</inlineCode>{` array when calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`. Warning: making this change may reduce conversion, as it will block customers from linking their accounts if their institution doesn't support the product. For more details, see `}<a parentName="p" {...{
            "href": "/docs/link/initializing-products/"
          }}>{`Choosing how to initialize products`}</a>{`.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`If the Item is at an OAuth-based institution, prompt the end user to allow Plaid to access the specific types of data your app needs. The end user should do this during the Link flow if they were unable to successfully complete the Link flow for the Item, or via Link's `}<a parentName="p" {...{
            "href": "/docs/link/update-mode"
          }}>{`update mode`}</a>{` if the Item has already been added.`}</p>
      </Checkbox>
      <Checkbox mdxType="Checkbox">
        <p>{`If receiving this error during `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` for update mode, and you are using the Item with the Assets product, you must instead create a new Item rather than updating the existing Item.`}</p>
      </Checkbox>
    </ErrorReference>
    <h4 {...{
      "id": "user_setup_required"
    }}><strong parentName="h4">{`USER_SETUP_REQUIRED`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='USER_SETUP_REQUIRED' subtitle="The user must log in directly to the financial institution and take some action before Plaid can access their accounts." client mdxType="ErrorReference">
      <h5 {...{
        "id": "link-user-experience-10"
      }}>{`Link user experience`}</h5>
      <p>{`Your user will be directed to a new window with the institution's homepage to complete the required setup steps. Link will then display the Institution Select pane for the user to connect a different account.`}</p>
      <h5 {...{
        "id": "common-causes-25"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`The institution requires special configuration steps before the user can link their account with Plaid. KeyBank, Morgan Stanley, Interactive Brokers, and Betterment are examples of institutions that require this setup.`}</li>
        <li parentName="ul">{`The user's account is not fully set up at their institution.`}</li>
        <li parentName="ul">{`The institution is blocking access due to an administrative task that requires completion. This error can arise for a number of reasons, the most common being:`}
          <ul parentName="li">
            <li parentName="ul">{`The user must agree to updated terms and conditions.`}</li>
            <li parentName="ul">{`The user must reset their password.`}</li>
            <li parentName="ul">{`The user must enter additional account information.`}</li>
          </ul>
        </li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-24"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='USER_SETUP_REQUIRED-1' mdxType="Checkbox">
        <p>{`Request that the user log in and complete their account setup directly at their institution.`}</p>
      </Checkbox>
      <p>{`Ask that the user log in to the bank, and confirm whether they are presented with some form of agreement page, modal, or some other actionable task. The user should also check their bank website for special settings to allow access for third-party apps, such as a "third party application password" or "allow third party access" setting.`}</p>
      <p>{`This can usually be done completely online, but there are some financial institutions that require the user to call a phone number, or access a physical branch.`}</p>
      <Checkbox checklistId='USER_SETUP_REQUIRED-2' mdxType="Checkbox">
        <p>{`Once completed, prompt the user to re-authenticate with Plaid Link.`}</p>
      </Checkbox>
      <p>{`If the user is still unable to log in to their institution, please submit a support ticket via the `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/support/new/financial-institutions/authentication-issues/invalid-creds"
        }}>{`Dashboard`}</a>{`. Which identifiers are useful varies by issue, but commonly include `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, `}<inlineCode parentName="p">{`institution_id`}</inlineCode>{`, and either `}<inlineCode parentName="p">{`link_session_id`}</inlineCode>{` or `}<inlineCode parentName="p">{`request_id`}</inlineCode>{`.`}</p>
    </ErrorReference>
    <h4 {...{
      "id": "user_input_timeout"
    }}><strong parentName="h4">{`USER_INPUT_TIMEOUT`}</strong></h4>
    <ErrorReference type='ITEM_ERROR' error='USER_INPUT_TIMEOUT' subtitle="The user did not complete a step in the Link flow, and it timed out." client mdxType="ErrorReference">
      <h5 {...{
        "id": "common-causes-26"
      }}>{`Common causes`}</h5>
      <ul>
        <li parentName="ul">{`Your user did not complete the account selection flow within five minutes.`}</li>
      </ul>
      <h5 {...{
        "id": "troubleshooting-steps-25"
      }}>{`Troubleshooting steps`}</h5>
      <Checkbox checklistId='USER_INPUT_TIMEOUT-1' mdxType="Checkbox">
        <p>{`Have your user attempt to link their account again.`}</p>
      </Checkbox>
    </ErrorReference>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "access_not_granted",
        level: 4,
        title: "ACCESS_NOT_GRANTED",
      },
{
        id: "instant_match_failed",
        level: 4,
        title: "INSTANT_MATCH_FAILED",
      },
{
        id: "insufficient_credentials",
        level: 4,
        title: "INSUFFICIENT_CREDENTIALS",
      },
{
        id: "invalid_credentials",
        level: 4,
        title: "INVALID_CREDENTIALS",
      },
{
        id: "invalid_mfa",
        level: 4,
        title: "INVALID_MFA",
      },
{
        id: "invalid_send_method",
        level: 4,
        title: "INVALID_SEND_METHOD",
      },
{
        id: "invalid_phone_number",
        level: 4,
        title: "INVALID_PHONE_NUMBER",
      },
{
        id: "invalid_otp",
        level: 4,
        title: "INVALID_OTP",
      },
{
        id: "invalid_updated_username",
        level: 4,
        title: "INVALID_UPDATED_USERNAME",
      },
{
        id: "item_concurrently_deleted",
        level: 4,
        title: "ITEM_CONCURRENTLY_DELETED",
      },
{
        id: "item_locked",
        level: 4,
        title: "ITEM_LOCKED",
      },
{
        id: "item_login_required",
        level: 4,
        title: "ITEM_LOGIN_REQUIRED",
      },
{
        id: "item_not_found",
        level: 4,
        title: "ITEM_NOT_FOUND",
      },
{
        id: "item_not_supported",
        level: 4,
        title: "ITEM_NOT_SUPPORTED",
      },
{
        id: "manual_verification_required",
        level: 4,
        title: "MANUAL_VERIFICATION_REQUIRED",
      },
{
        id: "mfa_not_supported",
        level: 4,
        title: "MFA_NOT_SUPPORTED",
      },
{
        id: "no_accounts",
        level: 4,
        title: "NO_ACCOUNTS",
      },
{
        id: "no_auth_accounts-or-no-depository-accounts",
        level: 4,
        title: "NO_AUTH_ACCOUNTS or no-depository-accounts",
      },
{
        id: "no_eligible_items",
        level: 4,
        title: "NO_ELIGIBLE_ITEMS",
      },
{
        id: "no_investment_accounts",
        level: 4,
        title: "NO_INVESTMENT_ACCOUNTS",
      },
{
        id: "no_liability_accounts",
        level: 4,
        title: "NO_LIABILITY_ACCOUNTS",
      },
{
        id: "password_reset_required",
        level: 4,
        title: "PASSWORD_RESET_REQUIRED",
      },
{
        id: "product_not_enabled",
        level: 4,
        title: "PRODUCT_NOT_ENABLED",
      },
{
        id: "product_not_ready",
        level: 4,
        title: "PRODUCT_NOT_READY",
      },
{
        id: "products_not_supported",
        level: 4,
        title: "PRODUCTS_NOT_SUPPORTED",
      },
{
        id: "user_setup_required",
        level: 4,
        title: "USER_SETUP_REQUIRED",
      },
{
        id: "user_input_timeout",
        level: 4,
        title: "USER_INPUT_TIMEOUT",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
